import * as React from 'react';
import {
    DefaultButton,
    DetailsList,
    DetailsListLayoutMode,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IColumn,
    IDropdownOption,
    Icon,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Selection,
    SelectionMode,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, ApiHelper } from '../../../ApiHelper';
import {
    createBulkPullRequestReminderEmailDraft,
    downloadRegressionEmailDraft,
} from '../../../features/regression-email/create-regression-email';
import {
    AutoPrDashboardItem,
    AutoPrDashboardResult,
    AutoCompleteAgentTaskPullRequestsResult,
    AutoPrSignal,
    PageShell,
    PublishAgentTaskPullRequestsResult,
    RepairAgentTaskPullRequestResult,
    buildAdoPullRequestUrl,
    formatDate,
    formatLocalDate,
    formatTaskListScope,
    getPullRequestRepairSuccessMessage,
    parseUtcDate as parsePrUtcDate,
    updateSearchParams,
    useCurrentService,
    useDocumentTitle,
} from '../shared';
import { PullRequestReviewerEmailButton } from '../PullRequestReviewerEmailButton';
import { PullRequestRepairProgress } from '../PullRequestRepairProgress';
import { AutoPrDetails } from '../AutoPrDetails';
import {
    getSignedInUserDisplayName,
    resolveReviewerEmailAddresses,
} from '../reviewerEmailResolution';

const PAGE_SIZE = 50;
const REVIEWER_PAGE_SIZE = 200;
const MAX_PUBLICATION_COUNT = 100;
const MAX_REPAIR_COUNT = 100;
const stages = [
    { key: 'draft', value: 0, label: 'Draft', description: 'Work or assessment outstanding before publication', count: 'draft' },
    { key: 'ready-to-publish', value: 5, label: 'Ready to publish', description: 'Drafts ready to open for review', count: 'readyToPublish' },
    { key: 'published', value: 1, label: 'Published', description: 'Technical work or assessment outstanding', count: 'published' },
    { key: 'signoff', value: 2, label: 'Blocked on signoff', description: 'Only required review approvals remain', count: 'blockedOnSignoff' },
    { key: 'ready', value: 3, label: 'Ready to complete', description: 'Eligible for auto-complete; ADO still checks merge gates', count: 'readyToComplete' },
    { key: 'completed', value: 4, label: 'Completed', description: 'Merged in Azure DevOps', count: 'completed' },
] as const;
const BUILD_VALIDATION = 0;
const MERGE_CONFLICT = 1;
const REVIEW_COMMENT = 2;
const REQUIRED_REVIEWER = 3;
const REVIEWER_SIGNOFF = 4;
const PROOF_OF_PRESENCE = 5;
const COMPLIANCE = 6;
const OTHER_POLICY = 7;
const OTHER_STATUS = 8;
const DRAFT_STATUSES = new Set([2, 3]);
const PUBLISHED_STATUSES = new Set([4, 6]);
const POLLED_STATUSES = new Set([0, 2, 4, 5, 6]);
const ACTIVE_REPAIR_JOB_STATUSES = new Set([0, 1, 2, 7]);
const FAILED_SIGNAL_STATES = new Set(['failed', 'error', 'rejected', 'broken']);
const VALIDATION_OPERATIONAL_STATES = new Set([
    'applicabilityunknown',
    'inspectionunavailable',
    'queueconfirmationunknown',
    'queuedenied',
    'retryexhausted',
    'sourceconflict',
]);

const attentionFilterOptions: IDropdownOption[] = [
    { key: '', text: 'All work progress' },
    { key: 'assessment-unavailable', text: 'Assessment unavailable' },
    { key: 'needs-repair', text: 'Needs repair' },
    { key: 'repair-active', text: 'Guardian repair active' },
    { key: 'validating', text: 'Validating' },
];

const attentionFilterLevels: Record<string, number[]> = {
    'assessment-unavailable': [0],
    'needs-repair': [4, 6],
    'repair-active': [5],
    validating: [2],
};

const legacyAttentionFilterAliases: Record<string, string> = {
    unknown: 'assessment-unavailable',
    'fix-required': 'needs-repair',
    'response-required': 'needs-repair',
    'validation-pending': 'validating',
    'waiting-for-signoff': 'awaiting-reviewer-approval',
};

const attentionStyles: Record<number, { background: string; color: string; label: string }> = {
    0: { background: '#edebe9', color: '#323130', label: 'Assessment unavailable' },
    1: { background: '#edebe9', color: '#323130', label: 'No attention needed' },
    2: { background: '#deecf9', color: '#005a9e', label: 'Validating' },
    3: { background: '#fff4ce', color: '#835c00', label: 'Awaiting reviewer approval' },
    4: { background: '#fed9cc', color: '#a4262c', label: 'Needs repair' },
    5: { background: '#e8d4f4', color: '#5c2d91', label: 'Guardian repair active' },
    6: { background: '#fde7e9', color: '#a80000', label: 'Needs repair' },
    7: { background: '#dff6dd', color: '#107c10', label: 'Ready to publish' },
    8: { background: '#dff6dd', color: '#107c10', label: 'Ready to complete' },
};

type BlockerCardTone = 'failure' | 'waiting' | 'response';

interface DashboardReason {
    key: string;
    label: string;
    scope?: string;
    tone: BlockerCardTone;
    signals: AutoPrSignal[];
}

const blockerCardStyles: Record<BlockerCardTone, {
    background: string;
    border: string;
    color: string;
}> = {
    failure: {
        background: '#fff7f7',
        border: '#d13438',
        color: '#a4262c',
    },
    waiting: {
        background: '#f3f9fd',
        border: '#0078d4',
        color: '#005a9e',
    },
    response: {
        background: '#fffaf0',
        border: '#986f0b',
        color: '#835c00',
    },
};

function AttentionBadge({ level, label }: { level: number; label?: string }) {
    const style = attentionStyles[level] ?? attentionStyles[0];
    return (
        <span style={{
            background: style.background,
            color: style.color,
            borderRadius: 12,
            display: 'inline-block',
            fontWeight: 600,
            padding: '3px 9px',
        }}>
            {label ?? style.label}
        </span>
    );
}

function formatPercent(value?: number | null) {
    return value === undefined || value === null ? '-' : `${value.toFixed(2)}%`;
}

function ageDays(value?: string | null) {
    if (!value) return null;
    const timestamp = Date.parse(value);
    if (Number.isNaN(timestamp)) return null;
    return Math.max(0, Math.floor((Date.now() - timestamp) / 86400000));
}

function formatPrLocalDate(value?: string | null) {
    return formatDate(value, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', second: '2-digit',
    });
}

function publicationTimestamp(value?: string | null) {
    const published = parsePrUtcDate(value);
    return published && published.toISOString() !== '0001-01-01T00:00:00.000Z'
        ? published.getTime()
        : null;
}

function formatPublicationAge(value?: string | null) {
    if (!value) return '-';
    const timestamp = publicationTimestamp(value);
    if (timestamp === null) return 'Unavailable';
    const hours = Math.max(0, Math.floor((Date.now() - timestamp) / 3600000));
    if (hours === 0) return 'Less than an hour ago';
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days === 1 ? '' : 's'} ago`;
}

function getNextEligibleCheck(item: AutoPrDashboardItem) {
    if (!POLLED_STATUSES.has(item.status)) return 'Not scheduled';
    if (!item.dateLastChecked) return 'Due now';
    const lastChecked = parsePrUtcDate(item.dateLastChecked);
    if (!lastChecked) return 'Unavailable';
    const lastCommit = parsePrUtcDate(item.lastCommitDate);
    if (item.health !== 4 && item.lastCommitDate && !lastCommit) return 'Unavailable';
    // Mirrors SPGetAgentTaskPullRequests eligibility, not the scheduler's execution time.
    const minutes = item.health === 4 ? 5
        : lastCommit && lastCommit.getTime() >= Date.now() - 7 * 86400000 ? 30 : 60;
    const nextCheck = new Date(lastChecked.getTime() + minutes * 60000);
    return nextCheck.getTime() <= Date.now() ? 'Due now' : formatPrLocalDate(nextCheck.toISOString());
}

function reviewerLabel(signal: AutoPrSignal) {
    return signal.displayName || signal.name;
}

/** Shows required reviewers and additional approvals consistently in the table and panel. */
function ReviewerList({ signals }: { signals: AutoPrSignal[] }) {
    const reviewers = signals.filter(signal =>
        (signal.category === REQUIRED_REVIEWER || signal.category === REVIEWER_SIGNOFF)
        && (signal.isRequired === true || signal.isSatisfied === true));
    if (reviewers.length === 0) {
        return <Text>-</Text>;
    }

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            {reviewers.map(reviewer => {
                const approved = reviewer.isSatisfied === true;
                const unknown = reviewer.isSatisfied === null
                    || reviewer.isSatisfied === undefined;
                const approvalLabel = reviewer.isRequired === true
                    ? 'Approved' : 'Approved (additional reviewer)';
                const approvalColor = reviewer.isRequired === true ? '#107c10' : '#9a7d00';
                const status = approved ? approvalLabel : unknown ? 'Unknown' : 'Not approved';

                return (
                    <Stack
                        key={`${reviewer.category}-${reviewer.signalKey}`}
                        horizontal
                        verticalAlign="start"
                        tokens={{ childrenGap: 6 }}
                    >
                        <Icon
                            iconName={approved ? 'CheckMark' : unknown ? 'Help' : 'Cancel'}
                            role="img"
                            aria-label={status}
                            title={status}
                            styles={{
                                root: {
                                    color: approved ? approvalColor : unknown ? '#605e5c' : '#a4262c',
                                    flexShrink: 0,
                                    marginTop: 2,
                                },
                            }}
                        />
                        <Text styles={{ root: { overflowWrap: 'anywhere' } }}>
                            {reviewerLabel(reviewer)}
                        </Text>
                    </Stack>
                );
            })}
        </Stack>
    );
}

function getRequiredReviewers(signals: AutoPrSignal[]): AutoPrSignal[] {
    return signals.filter(signal =>
        (signal.category === REQUIRED_REVIEWER || signal.category === REVIEWER_SIGNOFF)
        && signal.isRequired === true);
}

function getReminderReviewers(item: AutoPrDashboardItem): AutoPrSignal[] {
    const reviewers = new Map<string, AutoPrSignal>();
    getRequiredReviewers(item.signals)
        .filter(signal => signal.isSatisfied !== true)
        .forEach(signal => reviewers.set(signal.signalKey, signal));
    return [...reviewers.values()];
}

function getPrStateLabel(status: number) {
    switch (status) {
        case 2:
        case 3:
            return 'Draft';
        case 4:
        case 6:
            return 'Published';
        case 7:
            return 'Completed';
        case 8:
            return 'Abandoned';
        default:
            return 'Unknown';
    }
}

function getWorkProgress(item: AutoPrDashboardItem) {
    if (item.status === 7) {
        return {
            ...attentionStyles[1],
            level: 1,
            label: 'Completed',
            description: 'Azure DevOps reports that this PR is completed.',
        };
    }
    if (item.status === 8) {
        return {
            ...attentionStyles[1],
            level: 1,
            label: 'Abandoned',
            description: 'Azure DevOps reports that this PR is abandoned.',
        };
    }
    if (item.repairJobStatus !== null
        && item.repairJobStatus !== undefined
        && ACTIVE_REPAIR_JOB_STATUSES.has(item.repairJobStatus)) {
        return {
            ...attentionStyles[5],
            level: 5,
            description: 'Guardian is working on this PR. Current blockers remain listed below.',
        };
    }

    const style = attentionStyles[item.attentionLevel] ?? attentionStyles[0];
    const description = item.attentionLevel === 2
        ? 'Required checks remain outstanding. Queued work is not a successful validation or completed merge.'
        : item.attentionLevel === 3
            ? 'Required reviewers must approve the PR in Azure DevOps. This is a human review wait, not a running pipeline.'
            : item.attentionLevel === 4 || item.attentionLevel === 6
                ? 'Guardian or the author must address the applicable reasons below.'
                : item.attentionLevel === 5
                    ? 'Guardian is working on this PR. Current blockers remain listed below.'
                    : item.attentionLevel === 7
                        ? 'The draft is eligible for a live publication check. Publishing opens review; it does not complete or merge the PR.'
                        : item.attentionLevel === 8
                            ? 'Guardian can hand this PR to Azure DevOps auto-complete. Azure DevOps still enforces later merge gates.'
                            : item.attentionLevel === 0
                                ? 'Guardian could not fully assess this PR. Retry the inspection or check Azure DevOps.'
                                : 'No Guardian action is currently identified.';
    return { ...style, level: item.attentionLevel, description };
}

function normalizeSignalState(state?: string | null) {
    return state?.trim().toLowerCase() ?? '';
}

function isCapacityThrottled(signal: AutoPrSignal) {
    return signal.state?.toLowerCase() === 'concurrencythrottled';
}

function hasValidationOperationalState(signal: AutoPrSignal) {
    return VALIDATION_OPERATIONAL_STATES.has(normalizeSignalState(signal.state));
}

function hasExplicitFailureState(signal: AutoPrSignal) {
    return FAILED_SIGNAL_STATES.has(normalizeSignalState(signal.state));
}

function isActionableFailure(signal: AutoPrSignal) {
    return !isCapacityThrottled(signal)
        && !hasValidationOperationalState(signal)
        && (signal.isCurrentFailure
            || (signal.requiresIntervention
                && hasExplicitFailureState(signal)));
}

function isRequiredOrBlocking(signal: AutoPrSignal) {
    return signal.isRequired === true || signal.isBlocking === true;
}

function createReason(
    key: string,
    label: string,
    scope: string | undefined,
    tone: BlockerCardTone,
    signals: AutoPrSignal[],
): DashboardReason | null {
    return signals.length > 0 ? { key, label, scope, tone, signals } : null;
}

function getDashboardBlockers(item: AutoPrDashboardItem) {
    const blockers: DashboardReason[] = [];
    const actionScope = PUBLISHED_STATUSES.has(item.status) ? 'before auto-complete' : 'before publish';
    const buildSignals = item.signals.filter(signal =>
        signal.category === BUILD_VALIDATION && (signal.isSatisfied !== true || signal.isExpired));
    const addBlocker = (reason: DashboardReason | null) => {
        if (reason && (reason.scope !== 'before merge' || PUBLISHED_STATUSES.has(item.status))) {
            blockers.push(reason);
        }
    };

    addBlocker(createReason(
        'pipeline-failure',
        'Pipeline failure',
        actionScope,
        'failure',
        buildSignals.filter(isActionableFailure),
    ));
    addBlocker(createReason(
        'validation-expired',
        'Validation expired',
        'before merge',
        'waiting',
        buildSignals.filter(signal =>
            !isActionableFailure(signal)
            && (signal.isExpired || normalizeSignalState(signal.state) === 'expired')),
    ));
    addBlocker(createReason(
        'validation-pending',
        'Validation pending',
        actionScope,
        'waiting',
        buildSignals.filter(signal =>
            !isActionableFailure(signal)
            && isRequiredOrBlocking(signal)
            && ['pending', 'queued', 'running', 'inprogress', 'notstarted', 'queueconfirmationunknown']
                .includes(normalizeSignalState(signal.state))),
    ));
    addBlocker(createReason(
        'merge-conflict',
        'Merge conflict',
        actionScope,
        'failure',
        item.signals.filter(signal =>
            signal.category === MERGE_CONFLICT && signal.isSatisfied !== true),
    ));
    addBlocker(createReason(
        'review-comments',
        'Review comments',
        actionScope,
        'response',
        item.signals.filter(signal =>
            signal.category === REVIEW_COMMENT && signal.isSatisfied !== true),
    ));
    addBlocker(createReason(
        'reviewer-approval',
        'Reviewer approval',
        'before auto-complete',
        'response',
        item.signals.filter(signal =>
            (signal.isSatisfied !== true || signal.isExpired)
            && (((signal.category === REQUIRED_REVIEWER || signal.category === REVIEWER_SIGNOFF)
                && (signal.isRequired === true
                    || signal.requiresIntervention
                    || (signal.vote ?? 0) < 0))
                || (signal.category === OTHER_POLICY
                    && signal.isRequired === true
                    && signal.name.toLowerCase().includes('review')))),
    ));
    addBlocker(createReason(
        'compliance',
        'Compliance',
        'before merge',
        'response',
        item.signals.filter(signal =>
            signal.category === COMPLIANCE
            && signal.isSatisfied !== true
            && isRequiredOrBlocking(signal)),
    ));
    addBlocker(createReason(
        'proof-of-presence',
        'Proof of presence',
        'before merge',
        'response',
        item.signals.filter(signal =>
            signal.category === PROOF_OF_PRESENCE
            && signal.isSatisfied !== true
            && isRequiredOrBlocking(signal)),
    ));
    addBlocker(createReason(
        'required-policy',
        'Required policy',
        'before merge',
        'response',
        item.signals.filter(signal =>
            signal.category === OTHER_POLICY
            && signal.isSatisfied !== true
            && isRequiredOrBlocking(signal)
            && !(signal.isRequired === true && signal.name.toLowerCase().includes('review'))),
    ));
    addBlocker(createReason(
        'status-check',
        'Status check',
        'before merge',
        'response',
        item.signals.filter(signal =>
            signal.category === OTHER_STATUS
            && signal.isSatisfied !== true
            && isRequiredOrBlocking(signal)),
    ));

    const unavailableSignals = item.signals.filter(signal =>
        (signal.category === OTHER_STATUS && normalizeSignalState(signal.state) === 'unavailable')
        || (signal.category === BUILD_VALIDATION
            && ['applicabilityunknown', 'inspectionunavailable', 'sourceconflict']
                .includes(normalizeSignalState(signal.state))));
    if (!item.healthInspectionComplete || !item.reviewInspectionComplete
        || item.attentionLevel === 0 || unavailableSignals.length > 0) {
        blockers.push({
            key: 'assessment-unavailable',
            label: 'Assessment unavailable',
            scope: 'before Guardian action',
            tone: 'waiting',
            signals: unavailableSignals,
        });
    }

    return blockers;
}

function formatReasonScope(scope: string) {
    return `${scope.charAt(0).toUpperCase()}${scope.slice(1)}`;
}

function BlockerCards({ item, hideDraftCaption = false, hidePostPublicationCaptions = false, hideMergeBlockers = false, mergeOnly = false }: {
    item: AutoPrDashboardItem;
    hideDraftCaption?: boolean;
    hidePostPublicationCaptions?: boolean;
    hideMergeBlockers?: boolean;
    mergeOnly?: boolean;
}) {
    const reasons = getDashboardBlockers(item)
        .filter(reason => (!mergeOnly || reason.scope === 'before merge')
            && (!hideMergeBlockers || reason.scope !== 'before merge'));
    if (reasons.length === 0) {
        return <Text>-</Text>;
    }

    const renderReason = (reason: DashboardReason) => {
        const style = blockerCardStyles[reason.tone];
        return (
            <div key={reason.key}>
                <span
                    style={{
                        background: style.background,
                        border: `1px solid ${style.border}`,
                        borderRadius: 12,
                        color: style.color,
                        display: 'inline-block',
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '3px 8px',
                        whiteSpace: 'normal',
                    }}
                >
                    {reason.label}
                </span>
            </div>
        );
    };

    const groupedReasons = reasons.reduce<Map<string, DashboardReason[]>>((groups, reason) => {
        const scope = reason.scope ?? '';
        groups.set(scope, [...(groups.get(scope) ?? []), reason]);
        return groups;
    }, new Map());

    return (
        <div
            data-testid={`autopr-blockers-${item.prId}`}
            style={{
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            {[...groupedReasons.entries()].map(([scope, scopedReasons]) => (
                <div
                    key={scope || 'unscoped'}
                    role="group"
                    aria-label={scope ? `${formatReasonScope(scope)} blockers` : 'Other blockers'}
                >
                    {scope && !(hideDraftCaption && scope === 'before publish')
                        && !(hidePostPublicationCaptions && (scope === 'before auto-complete' || scope === 'before merge')) && (
                        <Text
                            block
                            styles={{
                                root: {
                                    color: '#605e5c',
                                    fontSize: 11,
                                    fontWeight: 600,
                                    marginBottom: 4,
                                },
                            }}
                        >
                            {formatReasonScope(scope)}
                        </Text>
                    )}
                    <div
                        style={{
                            alignItems: 'flex-start',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 6,
                        }}
                    >
                        {scopedReasons.map(renderReason)}
                    </div>
                </div>
            ))}
        </div>
    );
}

/** Shows scope-wide lifecycle counts and filtered PRs, with selection-bound publication and Resolve actions. */
export function AutoPrDashboardPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { serviceName } = useParams();
    const service = useCurrentService(serviceName);
    const [searchParams, setSearchParams] = useSearchParams();
    const requestedStage = searchParams.get('stage');
    const legacyAllActive = requestedStage === null && searchParams.get('view') === 'active';
    const stage = stages.find(choice => choice.key === requestedStage)
        ?? (searchParams.get('view') === 'completed' ? stages[5]
            : searchParams.get('state') === 'published' ? stages[2] : stages[0]);
    const view = !legacyAllActive && stage.key === 'completed' ? 1 : 0;
    const showProgress = legacyAllActive || stage.key === 'draft' || stage.key === 'published';
    const showReviewers = legacyAllActive || stage.key === 'published' || stage.key === 'signoff';
    const showPublishDate = !legacyAllActive
        && ['published', 'signoff', 'ready'].includes(stage.key);
    const canPublish = !legacyAllActive && (stage.key === 'draft' || stage.key === 'ready-to-publish');
    const canRepair = !legacyAllActive && (stage.key === 'draft' || stage.key === 'published');
    const canSelect = canPublish || canRepair || (!legacyAllActive && (stage.key === 'signoff' || stage.key === 'ready'));
    const page = Math.max(Number(searchParams.get('page') ?? '1') || 1, 1);
    const search = searchParams.get('search') ?? '';
    const prState = legacyAllActive && searchParams.get('state') === 'draft'
        ? 1
        : legacyAllActive && searchParams.get('state') === 'published'
            ? 2
            : 0;
    const reviewerSignalKey = showReviewers ? searchParams.get('reviewer') ?? '' : '';
    const requestedAttentionFilter = searchParams.get('attention') ?? '';
    const canonicalAttentionFilter =
        legacyAttentionFilterAliases[requestedAttentionFilter] ?? requestedAttentionFilter;
    const attentionFilterKey = showProgress
        && Object.prototype.hasOwnProperty.call(attentionFilterLevels, canonicalAttentionFilter)
        ? canonicalAttentionFilter
        : '';
    const selectedAttentionLevels = attentionFilterLevels[attentionFilterKey];
    const attentionFilterLabel = attentionFilterOptions.find(
        option => option.key === attentionFilterKey,
    )?.text;
    const taskListValue = searchParams.get('taskListId');
    const parsedTaskListId = taskListValue ? Number(taskListValue) : Number.NaN;
    const taskListId = Number.isSafeInteger(parsedTaskListId) && parsedTaskListId > 0
        ? parsedTaskListId
        : null;
    const selectedPrValue = searchParams.get('prId');
    const parsedSelectedPrId = selectedPrValue ? Number(selectedPrValue) : Number.NaN;
    const selectedPrId = Number.isSafeInteger(parsedSelectedPrId) && parsedSelectedPrId > 0
        ? parsedSelectedPrId
        : null;
    const [searchInput, setSearchInput] = React.useState(search);
    const [result, setResult] = React.useState<AutoPrDashboardResult | null>(null);
    const taskListName = result?.taskLists.find(option => option.taskListId === taskListId)?.name
        ?? (taskListId === null ? 'All task lists' : `Task list ${taskListId}`);
    const stageLabel = legacyAllActive ? 'All active' : stage.label;
    useDocumentTitle(`${selectedPrId ? `PR #${selectedPrId} - ` : ''}PR Dashboard - ${taskListName} - ${stageLabel}`);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [lastRefreshedAt, setLastRefreshedAt] = React.useState<Date | null>(null);
    const [deepLinkedItem, setDeepLinkedItem] = React.useState<AutoPrDashboardItem | null>(null);
    const [detailsLoading, setDetailsLoading] = React.useState(false);
    const [detailsError, setDetailsError] = React.useState<string | null>(null);
    const [refreshToken, setRefreshToken] = React.useState(0);
    const [columnResizeVersion, setColumnResizeVersion] = React.useState(0);
    const [publishedSortDescending, setPublishedSortDescending] = React.useState<boolean | null>(null);
    const [bulkEmailLoading, setBulkEmailLoading] = React.useState(false);
    const [bulkEmailMessage, setBulkEmailMessage] = React.useState<string | null>(null);
    const [selectedBulkPrIds, setSelectedBulkPrIds] = React.useState<number[]>([]);
    const [resolveLoading, setResolveLoading] = React.useState(false);
    const [resolveFeedback, setResolveFeedback] = React.useState<{
        message: string;
        type: MessageBarType;
    } | null>(null);
    const [publicationSelectionConfirmed, setPublicationSelectionConfirmed] =
        React.useState(false);
    const [publishDialogItems, setPublishDialogItems] =
        React.useState<AutoPrDashboardItem[]>([]);
    const [publishing, setPublishing] = React.useState(false);
    const [publishResult, setPublishResult] =
        React.useState<PublishAgentTaskPullRequestsResult | null>(null);
    const [autoCompleteDialogItems, setAutoCompleteDialogItems] =
        React.useState<AutoPrDashboardItem[]>([]);
    const [enablingAutoComplete, setEnablingAutoComplete] = React.useState(false);
    const [autoCompleteResult, setAutoCompleteResult] =
        React.useState<AutoCompleteAgentTaskPullRequestsResult | null>(null);
    const [repairDialogItems, setRepairDialogItems] = React.useState<AutoPrDashboardItem[]>([]);
    const [repairProgress, setRepairProgress] = React.useState<number | null>(null);
    const [repairOutcomes, setRepairOutcomes] = React.useState<Array<{
        prId: number;
        accepted: boolean;
        message: string;
    }>>([]);
    const repairing = repairProgress !== null;
    const requestId = React.useRef(0);
    const resolveRequestId = React.useRef(0);
    const bulkRepairRequestId = React.useRef(0);
    const selectedPrIdRef = React.useRef<number | null>(selectedPrId);
    const scopeLinkToRestore = React.useRef<HTMLElement | null>(null);
    const panelOpenedFromDashboard = React.useRef(false);
    const previousSelectedPrId = React.useRef<number | null>(selectedPrId);
    const selectionChangeIsProgrammatic = React.useRef(false);
    const bulkSelection = React.useMemo(() => {
        const selection = new Selection({
            getKey: item => String((item as AutoPrDashboardItem).prId),
            onSelectionChanged: () => {
                setSelectedBulkPrIds((selection.getSelection() as AutoPrDashboardItem[])
                    .map(item => item.prId));
            },
        });
        return selection;
    }, []);
    const pageSize = view === 0 && reviewerSignalKey
        ? REVIEWER_PAGE_SIZE
        : PAGE_SIZE;
    React.useEffect(() => setSearchInput(search), [search]);

    React.useEffect(() => {
        if (requestedAttentionFilter && requestedAttentionFilter !== attentionFilterKey) {
            const next = new URLSearchParams(searchParams);
            if (attentionFilterKey) {
                next.set('attention', attentionFilterKey);
            } else {
                next.delete('attention');
            }
            setSearchParams(next, { replace: true });
        }
    }, [attentionFilterKey, requestedAttentionFilter, searchParams, setSearchParams]);

    React.useEffect(() => {
        setResult(null);
        setError(null);
        setLastRefreshedAt(null);
        selectionChangeIsProgrammatic.current = true;
        bulkSelection.setItems([], true);
        selectionChangeIsProgrammatic.current = false;
        setSelectedBulkPrIds([]);
        setPublicationSelectionConfirmed(false);
        setPublishDialogItems([]);
        setPublishResult(null);
        setAutoCompleteDialogItems([]);
        setAutoCompleteResult(null);
    }, [bulkSelection, serviceName]);

    React.useEffect(() => {
        bulkRepairRequestId.current++;
        setRepairDialogItems([]);
        setRepairProgress(null);
        setRepairOutcomes([]);
        return () => { bulkRepairRequestId.current++; };
    }, [serviceName, stage.value, legacyAllActive, taskListId, search, reviewerSignalKey, page, attentionFilterKey]);

    React.useEffect(() => {
        if (!serviceName) return;
        const currentRequestId = ++requestId.current;
        const query = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
        });
        if (legacyAllActive) {
            query.set('view', '0');
            if (prState !== 0) query.set('prState', String(prState));
        } else {
            query.set('stage', String(stage.value));
        }
        if (reviewerSignalKey) {
            query.set('reviewerSignalKey', reviewerSignalKey);
        }
        if (taskListId !== null) query.set('taskListId', String(taskListId));
        if (search.trim()) query.set('search', search.trim());

        setLoading(true);
        setError(null);
        selectionChangeIsProgrammatic.current = true;
        bulkSelection.setItems([], true);
        selectionChangeIsProgrammatic.current = false;
        setSelectedBulkPrIds([]);
        setPublicationSelectionConfirmed(false);
        ApiHelper(`/api/service/${serviceName}/agenttasks/autopr?${query.toString()}`)
            .then((response: AutoPrDashboardResult) => {
                if (requestId.current !== currentRequestId) return;
                setResult(response);
                setLastRefreshedAt(new Date());
                const selectableItems = selectedAttentionLevels === undefined
                    ? response.items
                    : response.items.filter(item =>
                        selectedAttentionLevels.includes(getWorkProgress(item).level));
                selectionChangeIsProgrammatic.current = true;
                bulkSelection.setItems(selectableItems, true);
                if (view === 0 && reviewerSignalKey) {
                    bulkSelection.setAllSelected(true);
                }
                selectionChangeIsProgrammatic.current = false;
                setSelectedBulkPrIds(
                    (bulkSelection.getSelection() as AutoPrDashboardItem[])
                        .map(item => item.prId),
                );
                setLoading(false);
            })
            .catch((reason: Error) => {
                if (requestId.current !== currentRequestId) return;
                setError(reason.message || 'Failed to load the PR dashboard.');
                setLoading(false);
            });
    }, [
        bulkSelection,
        page,
        pageSize,
        prState,
        refreshToken,
        reviewerSignalKey,
        search,
        serviceName,
        taskListId,
        view,
        stage.value,
        legacyAllActive,
    ]);

    const selectedFromPage = React.useMemo(
        () => result?.items.find(item => item.prId === selectedPrId) ?? null,
        [result?.items, selectedPrId],
    );

    React.useEffect(() => {
        let isCurrent = true;
        setDeepLinkedItem(null);
        setDetailsError(null);

        if (loading && !result) {
            setDetailsLoading(true);
            return () => {
                isCurrent = false;
            };
        }

        if (!serviceName || selectedPrId === null
            || (selectedFromPage && !selectedFromPage.repairWorkItemId)) {
            setDetailsLoading(false);
            return () => {
                isCurrent = false;
            };
        }

        setDetailsLoading(true);
        ApiHelper(`/api/service/${serviceName}/agenttasks/autopr/${selectedPrId}`)
            .then((response: AutoPrDashboardItem) => {
                if (!isCurrent) return;
                if (response.prId !== selectedPrId) {
                    throw new Error(`The PR ${selectedPrId} detail response did not match the selection.`);
                }
                setDeepLinkedItem(response);
                setDetailsLoading(false);
            })
            .catch((reason: Error) => {
                if (!isCurrent) return;
                setDetailsError(reason.message || `Failed to load PR ${selectedPrId}.`);
                setDetailsLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [loading, refreshToken, result, selectedFromPage, selectedPrId, serviceName]);

    const selected = deepLinkedItem?.prId === selectedPrId ? deepLinkedItem : selectedFromPage;
    const visibleItems = React.useMemo(() => {
        const items = result?.items ?? [];
        return selectedAttentionLevels === undefined
            ? items
            : items.filter(item => selectedAttentionLevels.includes(getWorkProgress(item).level));
    }, [result?.items, selectedAttentionLevels]);

    const sortedVisibleItems = React.useMemo(() => {
        if (!showPublishDate || publishedSortDescending === null) return visibleItems;
        return [...visibleItems].sort((left, right) => {
            const leftTime = publicationTimestamp(left.publishDate);
            const rightTime = publicationTimestamp(right.publishDate);
            if (leftTime === null) return rightTime === null ? 0 : 1;
            if (rightTime === null) return -1;
            return publishedSortDescending ? rightTime - leftTime : leftTime - rightTime;
        });
    }, [publishedSortDescending, showPublishDate, visibleItems]);

    React.useEffect(() => {
        if (!canSelect) {
            selectionChangeIsProgrammatic.current = true;
            bulkSelection.setItems([], true);
            selectionChangeIsProgrammatic.current = false;
            setSelectedBulkPrIds([]);
            setPublicationSelectionConfirmed(false);
            return;
        }

        const selectedIds = new Set(selectedBulkPrIds);
        selectionChangeIsProgrammatic.current = true;
        bulkSelection.setItems(visibleItems, true);
        if (publicationSelectionConfirmed) {
            visibleItems.forEach(item => {
                if (selectedIds.has(item.prId)) {
                    bulkSelection.setKeySelected(String(item.prId), true, false);
                }
            });
        } else if (reviewerSignalKey) {
            bulkSelection.setAllSelected(true);
        }
        selectionChangeIsProgrammatic.current = false;
        setSelectedBulkPrIds((bulkSelection.getSelection() as AutoPrDashboardItem[])
            .map(item => item.prId));
    }, [
        bulkSelection,
        publicationSelectionConfirmed,
        result?.items,
        reviewerSignalKey,
        view,
        canSelect,
        visibleItems,
    ]);

    const closeDetails = React.useCallback(() => {
        if (panelOpenedFromDashboard.current) {
            navigate(-1);
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('prId');
        setSearchParams(nextSearchParams, { replace: true });
    }, [navigate, searchParams, setSearchParams]);

    React.useEffect(() => {
        if (previousSelectedPrId.current !== null && selectedPrId === null) {
            window.requestAnimationFrame(() => scopeLinkToRestore.current?.focus());
        }
        previousSelectedPrId.current = selectedPrId;
    }, [selectedPrId]);

    React.useEffect(() => {
        selectedPrIdRef.current = selectedPrId;
        resolveRequestId.current++;
        setResolveFeedback(null);
        setResolveLoading(false);
    }, [selectedPrId]);

    const reviewerGroupOptions = React.useMemo<IDropdownOption[]>(() => [
        { key: '', text: 'All reviewer groups' },
        ...(result?.reviewerGroups ?? []).map(group => ({
            key: group.signalKey,
            text: `${group.displayName} (${group.pendingCount})`,
        })),
    ], [result?.reviewerGroups]);

    const taskListOptions = React.useMemo<IDropdownOption[]>(() => {
        const taskLists = result?.taskLists ?? [];
        const nameCounts = new Map<string, number>();
        taskLists.forEach(option => {
            const normalizedName = option.name.trim().toLocaleLowerCase();
            nameCounts.set(normalizedName, (nameCounts.get(normalizedName) ?? 0) + 1);
        });
        const options = taskLists.map(option => {
            const name = option.name.trim() || `Task list ${option.taskListId}`;
            const duplicateName = (nameCounts.get(name.toLocaleLowerCase()) ?? 0) > 1;
            return {
                key: option.taskListId,
                text: duplicateName
                    ? `${name} - generation ${option.generation} (#${option.taskListId})`
                    : name,
            };
        });
        if (taskListId !== null && !options.some(option => option.key === taskListId)) {
            options.unshift({ key: taskListId, text: `Task list ${taskListId}` });
        }
        return [{ key: '', text: 'All task lists' }, ...options];
    }, [result?.taskLists, taskListId]);

    const draftBulkReminder = React.useCallback(async () => {
        if (!serviceName || loading || error) return;

        setBulkEmailLoading(true);
        setBulkEmailMessage(null);
        try {
            const selectedIds = new Set(selectedBulkPrIds);
            const selectedItems = (result?.items ?? []).filter(item =>
                selectedIds.has(item.prId));
            if (selectedItems.length === 0) {
                throw new Error('Select at least one PR before creating a draft.');
            }
            const draftItems = selectedItems.map(item => {
                const prUrl = buildAdoPullRequestUrl(service, item.prId);
                if (!prUrl) {
                    throw new Error(`Could not build the Azure DevOps URL for PR ${item.prId}.`);
                }
                return {
                    item,
                    prUrl,
                    reviewers: getReminderReviewers(item),
                };
            });
            const eligibleItems = draftItems.filter(item => item.reviewers.length > 0);
            const skippedItems = draftItems.filter(item => item.reviewers.length === 0);
            if (eligibleItems.length === 0) {
                throw new Error('No selected PRs have pending required reviewers to email.');
            }

            const reviewersBySignalKey = new Map<string, {
                signalKey: string;
                name: string;
                emailAddress: string | null;
                pullRequests: Array<{
                    taskListId: number;
                    taskId: number;
                    prId: number;
                }>;
            }>();
            eligibleItems.forEach(({ item, reviewers }) => {
                reviewers.forEach(reviewer => {
                    const existing = reviewersBySignalKey.get(reviewer.signalKey);
                    const pullRequest = {
                        taskListId: item.taskListId,
                        taskId: item.taskId,
                        prId: item.prId,
                    };
                    if (existing) {
                        existing.emailAddress ||= reviewer.emailAddress?.trim() || null;
                        if (!existing.pullRequests.some(reference =>
                            reference.taskListId === pullRequest.taskListId
                            && reference.taskId === pullRequest.taskId
                            && reference.prId === pullRequest.prId)) {
                            existing.pullRequests.push(pullRequest);
                        }
                        return;
                    }
                    reviewersBySignalKey.set(reviewer.signalKey, {
                        signalKey: reviewer.signalKey,
                        name: reviewerLabel(reviewer),
                        emailAddress: reviewer.emailAddress?.trim() || null,
                        pullRequests: [pullRequest],
                    });
                });
            });
            const resolution = await resolveReviewerEmailAddresses(
                serviceName,
                [...reviewersBySignalKey.values()],
            );
            const resolvedBySignalKey = new Map(
                resolution.reviewers.map(reviewer => [reviewer.signalKey, reviewer]),
            );
            const draftGroups = new Map<string, {
                reviewers: Array<{
                    name: string;
                    email: string | null;
                }>;
                pullRequests: Array<{
                    prId: number;
                    scope: string;
                    prUrl: string;
                    attentionSummary: string;
                    ageDays: number | null;
                }>;
            }>();
            eligibleItems.forEach(({ item, prUrl, reviewers }) => {
                const resolvedReviewers = reviewers
                    .map(reviewer => {
                        const resolved = resolvedBySignalKey.get(reviewer.signalKey);
                        return {
                            signalKey: reviewer.signalKey,
                            name: resolved?.name || reviewerLabel(reviewer),
                            email: resolved?.emailAddress || null,
                        };
                    })
                    .sort((left, right) => left.signalKey.localeCompare(right.signalKey));
                const groupKey = resolvedReviewers
                    .map(reviewer => reviewer.signalKey.toLowerCase())
                    .join('\u001f');
                const pullRequest = {
                    prId: item.prId,
                    scope: item.scope,
                    prUrl,
                    attentionSummary: item.attentionSummary,
                    ageDays: ageDays(item.dateCreated),
                };
                const group = draftGroups.get(groupKey);
                if (group) {
                    group.pullRequests.push(pullRequest);
                    return;
                }
                draftGroups.set(groupKey, {
                    reviewers: resolvedReviewers.map(({ name, email }) => ({ name, email })),
                    pullRequests: [pullRequest],
                });
            });
            draftGroups.forEach(group => {
                const draft = createBulkPullRequestReminderEmailDraft({
                    senderName: getSignedInUserDisplayName(),
                    reviewers: group.reviewers,
                    pullRequests: group.pullRequests,
                });
                downloadRegressionEmailDraft(draft);
            });
            const unresolvedCount = resolution.reviewers.filter(
                reviewer => !reviewer.emailAddress,
            ).length;
            const lookupWarning = resolution.lookupFailed
                ? ' The secondary email lookup failed; unresolved reviewer names remain in the To fields.'
                : unresolvedCount > 0
                    ? ' Resolve the remaining reviewer names in Outlook before sending.'
                    : '';
            const skippedWarning = skippedItems.length > 0
                ? ` Skipped ${skippedItems.length} selected PR${
                    skippedItems.length === 1 ? '' : 's'
                } without pending required reviewers: ${
                    skippedItems.map(({ item }) => item.prId).join(', ')
                }.`
                : '';
            setBulkEmailMessage(
                `Downloaded ${draftGroups.size} reviewer reminder draft${
                    draftGroups.size === 1 ? '' : 's'
                } for ${eligibleItems.length} PR${
                    eligibleItems.length === 1 ? '' : 's'
                }, grouped by recipient set.${skippedWarning}${lookupWarning}`,
            );
        } catch (reason) {
            setBulkEmailMessage(
                reason instanceof Error ? reason.message : 'Could not create the reviewer reminder draft.',
            );
        } finally {
            setBulkEmailLoading(false);
        }
    }, [
        result?.items,
        selectedBulkPrIds,
        service,
        serviceName,
        error,
        loading,
    ]);

    const panelInteractionsDisabled = loading || Boolean(error)
        || detailsLoading || Boolean(detailsError);
    const selectedBulkItems = React.useMemo(() => {
        const selectedIds = new Set(selectedBulkPrIds);
        return visibleItems.filter(item => selectedIds.has(item.prId));
    }, [selectedBulkPrIds, visibleItems]);
    const selectedDraftItems = React.useMemo(
        () => selectedBulkItems.filter(item => DRAFT_STATUSES.has(item.status)),
        [selectedBulkItems],
    );
    const selectedNonDraftCount = selectedBulkItems.length - selectedDraftItems.length;
    const hasPublicationIntent =
        reviewerSignalKey.length === 0 || publicationSelectionConfirmed;

    const publishPullRequests = React.useCallback(async () => {
        if (!serviceName || publishing || publishDialogItems.length === 0) return;

        const submittedItems = publishDialogItems;
        setPublishing(true);
        try {
            const response = await ApiHelper(
                `/api/service/${serviceName}/agenttasks/autopr:publish`,
                'POST',
                {
                    pullRequests: submittedItems.map(item => ({
                        taskListId: item.taskListId,
                        taskId: item.taskId,
                        prId: item.prId,
                    })),
                },
            ) as PublishAgentTaskPullRequestsResult;
            setPublishResult(response);
            setPublishDialogItems([]);
            setPublicationSelectionConfirmed(false);
            setRefreshToken(value => value + 1);
        } catch (reason) {
            const message = reason instanceof Error
                ? reason.message
                : 'The publication request failed.';
            setPublishResult({
                requestedCount: submittedItems.length,
                publishedCount: 0,
                notPublishedCount: submittedItems.length,
                outcomes: submittedItems.map(item => ({
                    taskListId: item.taskListId,
                    taskId: item.taskId,
                    prId: item.prId,
                    scope: item.scope,
                    published: false,
                    code: 'PublishFailed',
                    message: `Could not publish PR ${item.prId}. ${message}`,
                })),
            });
            setPublishDialogItems([]);
        } finally {
            setPublishing(false);
        }
    }, [publishDialogItems, publishing, serviceName]);

    const enableAutoComplete = React.useCallback(async () => {
        if (!serviceName || enablingAutoComplete || autoCompleteDialogItems.length === 0) return;
        const submittedItems = autoCompleteDialogItems;
        setEnablingAutoComplete(true);
        try {
            const response = await ApiHelper<AutoCompleteAgentTaskPullRequestsResult>(
                `/api/service/${serviceName}/agenttasks/autopr:autocomplete`, 'POST', {
                    pullRequests: submittedItems.map(item => ({
                        taskListId: item.taskListId, taskId: item.taskId, prId: item.prId,
                    })),
                },
            );
            setAutoCompleteResult(response);
            setRefreshToken(value => value + 1);
        } catch (reason) {
            const message = reason instanceof Error ? reason.message : 'The auto-complete request failed.';
            setAutoCompleteResult({
                requestedCount: submittedItems.length, enabledCount: 0, notEnabledCount: submittedItems.length,
                outcomes: submittedItems.map(item => ({
                    taskListId: item.taskListId, taskId: item.taskId, prId: item.prId, scope: item.scope,
                    enabled: false, code: 'AutoCompleteFailed',
                    message: `Could not enable auto-complete for PR ${item.prId}. ${message}`,
                })),
            });
        } finally {
            setAutoCompleteDialogItems([]);
            setEnablingAutoComplete(false);
        }
    }, [autoCompleteDialogItems, enablingAutoComplete, serviceName]);

    const repairSelectedPullRequests = React.useCallback(async () => {
        if (!serviceName || repairing || repairDialogItems.length === 0) return;
        const submittedItems = repairDialogItems;
        const currentRequestId = ++bulkRepairRequestId.current;
        const outcomes: typeof repairOutcomes = [];
        setRepairProgress(0);
        setRepairOutcomes([]);
        for (const item of submittedItems) {
            if (bulkRepairRequestId.current !== currentRequestId) return;
            try {
                const response = await ApiHelper<RepairAgentTaskPullRequestResult>(
                    `/api/service/${serviceName}/agenttasklists/${item.taskListId}/tasks/${item.taskId}/pullrequests/${item.prId}/resolve`,
                    'POST',
                    null,
                );
                if (bulkRepairRequestId.current !== currentRequestId) return;
                outcomes.push({
                    prId: item.prId,
                    accepted: true,
                    message: getPullRequestRepairSuccessMessage(response, item.prId),
                });
            } catch (reason) {
                if (bulkRepairRequestId.current !== currentRequestId) return;
                outcomes.push({
                    prId: item.prId,
                    accepted: false,
                    message: `PR ${item.prId}: ${reason instanceof Error ? reason.message : 'Failed to request repair.'}`,
                });
            }
            setRepairProgress(outcomes.length);
        }
        setRepairOutcomes(outcomes);
        setRepairDialogItems([]);
        setRepairProgress(null);
        setRefreshToken(value => value + 1);
    }, [repairDialogItems, repairing, serviceName]);

    /** Reports the accepted Resolve action and ignores responses belonging to a superseded PR selection. */
    const resolvePullRequest = React.useCallback(async () => {
        if (!serviceName
            || !selected?.canResolve
            || resolveLoading
            || repairing
            || panelInteractionsDisabled) {
            return;
        }
        const resolvingPrId = selected.prId;
        const currentResolveRequestId = ++resolveRequestId.current;
        setResolveLoading(true);
        setResolveFeedback(null);
        try {
            const result = await ApiHelper<RepairAgentTaskPullRequestResult>(
                `/api/service/${serviceName}/agenttasklists/${selected.taskListId}/tasks/${selected.taskId}/pullrequests/${selected.prId}/resolve`,
                'POST',
                null,
            );
            if (resolveRequestId.current !== currentResolveRequestId
                || selectedPrIdRef.current !== resolvingPrId) {
                return;
            }
            setResolveFeedback({
                message: getPullRequestRepairSuccessMessage(result, resolvingPrId),
                type: MessageBarType.success,
            });
            setRefreshToken(value => value + 1);
        } catch (reason) {
            if (resolveRequestId.current !== currentResolveRequestId
                || selectedPrIdRef.current !== resolvingPrId) {
                return;
            }
            setResolveFeedback({
                message: reason instanceof Error
                    ? reason.message
                    : 'Failed to resolve the pull request.',
                type: MessageBarType.error,
            });
            const errorCode = reason instanceof ApiError
                && typeof reason.body === 'object'
                && reason.body !== null
                && 'code' in reason.body
                && typeof reason.body.code === 'string'
                    ? reason.body.code
                    : null;
            if (errorCode === 'PullRequestResolutionAlreadyRunning'
                || errorCode === 'PullRequestRepairNotActionable') {
                setRefreshToken(value => value + 1);
            }
        } finally {
            if (resolveRequestId.current === currentResolveRequestId
                && selectedPrIdRef.current === resolvingPrId) {
                setResolveLoading(false);
            }
        }
    }, [
        panelInteractionsDisabled,
        resolveLoading,
        repairing,
        selected,
        serviceName,
    ]);

    const activeColumns = React.useMemo<IColumn[]>(() => [
        {
            key: 'pr',
            name: 'PR',
            minWidth: 105,
            onRender: (item: AutoPrDashboardItem) => {
                const prUrl = buildAdoPullRequestUrl(service, item.prId);
                return prUrl
                    ? (
                        <a
                            href={prUrl}
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`PR #${item.prId} (opens in new tab)`}
                            style={{ alignItems: 'center', display: 'inline-flex', gap: 4 }}
                        >
                            #{item.prId}
                            <Icon iconName="OpenInNewWindow" aria-hidden={true} />
                        </a>
                    )
                    : <Text>#{item.prId}</Text>;
            },
        },
        {
            key: 'scope',
            name: 'Scope',
            minWidth: 220,
            isResizable: true,
            onRender: (item: AutoPrDashboardItem) => {
                const nextSearchParams = new URLSearchParams(searchParams);
                nextSearchParams.set('prId', String(item.prId));
                return (
                    <Link
                        to={`${location.pathname}?${nextSearchParams.toString()}`}
                        title={item.scope}
                        onClick={event => {
                            scopeLinkToRestore.current = event.currentTarget;
                            panelOpenedFromDashboard.current = true;
                        }}
                    >
                        {formatTaskListScope(item.scope)}
                    </Link>
                );
            },
        },
        {
            key: 'status',
            name: 'Work progress',
            minWidth: 185,
            onRender: (item: AutoPrDashboardItem) => {
                const progress = getWorkProgress(item);
                return <AttentionBadge level={progress.level} label={progress.label} />;
            },
        },
        {
            key: 'prState',
            name: 'PR lifecycle',
            minWidth: 90,
            onRender: (item: AutoPrDashboardItem) => getPrStateLabel(item.status),
        },
        {
            key: 'blocked',
            name: 'Blocked on',
            minWidth: 230,
            isResizable: true,
            onRender: (item: AutoPrDashboardItem) => (
                <BlockerCards
                    item={item}
                    hideDraftCaption={stage.key === 'draft'}
                    hidePostPublicationCaptions={!legacyAllActive}
                    hideMergeBlockers={!legacyAllActive && stage.key === 'published'}
                    mergeOnly={!legacyAllActive && stage.key === 'ready'}
                />
            ),
        },
        {
            key: 'signedOff',
            name: 'Reviewers',
            minWidth: 190,
            isResizable: true,
            onRender: (item: AutoPrDashboardItem) => <ReviewerList signals={item.signals} />,
        },
        {
            key: 'gain',
            name: 'Estimated gain',
            minWidth: 105,
            onRender: (item: AutoPrDashboardItem) => formatPercent(item.estimatedGain),
        },
        {
            key: 'published',
            name: 'Published',
            minWidth: 140,
            isSorted: publishedSortDescending !== null,
            isSortedDescending: publishedSortDescending ?? false,
            showSortIconWhenUnsorted: true,
            onColumnClick: () => setPublishedSortDescending(value => value === null || !value),
            onRender: (item: AutoPrDashboardItem) => (
                <span title={item.publishDate
                    ? `First observed published: ${formatPrLocalDate(item.publishDate)}`
                    : 'Publication has not been observed.'}>
                    {formatPublicationAge(item.publishDate)}
                </span>
            ),
        },
        {
            key: 'age',
            name: 'Age',
            minWidth: 70,
            onRender: (item: AutoPrDashboardItem) => {
                const age = ageDays(item.dateCreated);
                return age === null ? '-' : `${age}d`;
            },
        },
    ], [location.pathname, searchParams, service, stage.key, legacyAllActive, publishedSortDescending]);

    const completedColumns = React.useMemo<IColumn[]>(() => [
        activeColumns[0],
        activeColumns[1],
        {
            key: 'outcome',
            name: 'Outcome',
            minWidth: 120,
            onRender: () => 'Completed',
        },
        {
            key: 'completed',
            name: 'Completed',
            minWidth: 140,
            onRender: (item: AutoPrDashboardItem) => formatLocalDate(item.completedDate),
        },
        {
            key: 'deployment',
            name: 'Deployment',
            minWidth: 100,
            onRender: (item: AutoPrDashboardItem) => item.deploymentStatus ?? '-',
        },
        activeColumns.find(column => column.key === 'gain')!,
        {
            key: 'measurement',
            name: 'Measurement',
            minWidth: 130,
            onRender: (item: AutoPrDashboardItem) => item.measurementStatus || 'Not populated',
        },
        {
            key: 'realized',
            name: 'Realized gain',
            minWidth: 110,
            onRender: (item: AutoPrDashboardItem) => formatPercent(item.realizedGain),
        },
        {
            key: 'variance',
            name: 'Variance',
            minWidth: 90,
            onRender: (item: AutoPrDashboardItem) => formatPercent(item.variance),
        },
    ], [activeColumns]);
    const handleColumnResize = React.useCallback(() => {
        setColumnResizeVersion(version => version + 1);
    }, []);
    const listProps = React.useMemo(
        () => ({ version: { columnResizeVersion } }),
        [columnResizeVersion],
    );
    const hasActiveFilters =
        prState !== 0
        || reviewerSignalKey.length > 0
        || attentionFilterKey.length > 0
        || search.length > 0;
    const bulkInteractionsDisabled = loading || error !== null;
    const showSelection = canSelect && !bulkInteractionsDisabled;
    const clearFilters = React.useCallback(() => {
        setSearchInput('');
        updateSearchParams(searchParams, setSearchParams, {
            state: null,
            reviewer: null,
            attention: null,
            search: null,
            page: null,
            prId: null,
        });
    }, [searchParams, setSearchParams]);
    const lastRefreshedLabel = lastRefreshedAt?.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
    });
    const selectedProgress = selected ? getWorkProgress(selected) : null;
    const selectedBlockers = selected ? getDashboardBlockers(selected) : [];

    return (
        <PageShell>
            <Stack horizontal wrap horizontalAlign="space-between" verticalAlign="center">
                <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 16 }}>
                    <Text as="h1" variant="xxLarge" styles={{ root: { margin: 0 } }}>PR Dashboard</Text>
                    <Dropdown
                        ariaLabel="Task list"
                        selectedKey={taskListId ?? ''}
                        options={taskListOptions}
                        onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                        taskListId: option?.key ? String(option.key) : null,
                        page: null,
                        prId: null,
                    })}
                        styles={{ root: { minWidth: 240 }, title: { fontWeight: 600 } }}
                    />
                </Stack>
                <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign="center">
                    {lastRefreshedLabel && (
                        <Text variant="small">Saved results loaded {lastRefreshedLabel}</Text>
                    )}
                    <DefaultButton
                        text="Refresh"
                        title="Reload saved Guardian results. Azure DevOps is checked by the background observer."
                        disabled={loading}
                        onClick={() => setRefreshToken(value => value + 1)}
                    />
                    <DefaultButton text="Back to Agent Tasks" onClick={() => navigate(`/service/${serviceName}/agenttasks`)} />
                </Stack>
            </Stack>

                <Stack horizontal wrap tokens={{ childrenGap: 10 }} role="group" aria-label="PR stages">
                    {stages.map(choice => (
                        <button
                            key={choice.key}
                            type="button"
                            aria-pressed={!legacyAllActive && choice.key === stage.key}
                            onClick={() => updateSearchParams(searchParams, setSearchParams, {
                                stage: choice.key, view: null, state: null, reviewer: null,
                                attention: null, page: null, prId: null,
                            })}
                            style={{
                                background: !legacyAllActive && choice.key === stage.key ? '#eff6fc' : '#fff',
                                border: `2px solid ${!legacyAllActive && choice.key === stage.key ? '#0078d4' : '#d1d1d1'}`,
                                borderRadius: 4,
                                flex: '1 1 220px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                cursor: 'pointer',
                                textAlign: 'left',
                                font: 'inherit',
                                padding: '12px 14px',
                            }}
                        >
                            <Text block variant="large" styles={{ root: { fontWeight: 600, whiteSpace: 'nowrap' } }}>
                                {choice.label}: {result?.stageCounts?.[choice.count] ?? 0}
                            </Text>
                            <Text block variant="small" styles={{ root: { color: '#605e5c' } }}>
                                {choice.description}
                            </Text>
                        </button>
                    ))}
                </Stack>
            {(result?.stageCounts?.unclassifiedActive ?? 0) > 0 && (
                <MessageBar>
                    {result?.stageCounts.unclassifiedActive} PRs have an unknown or legacy lifecycle.
                    {' '}<Link to={`${location.pathname}?${new URLSearchParams({
                        view: 'active', ...(taskListId === null ? {} : { taskListId: String(taskListId) }),
                    })}`}>View all active PRs</Link>
                </MessageBar>
            )}

            <div role="search" aria-label="PR filters" style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 8,
                paddingLeft: 12 + (showSelection ? 48 : 0),
            }}>
                {showReviewers && (
                        <Dropdown
                            label="Required reviewers"
                            selectedKey={reviewerSignalKey}
                            options={reviewerGroupOptions}
                            onChange={(_, option) => updateSearchParams(searchParams, setSearchParams, {
                                reviewer: option?.key ? String(option.key) : null,
                                page: null,
                                prId: null,
                            })}
                            styles={{ root: { minWidth: 310 } }}
                        />
                )}
                {showProgress && (
                        <Dropdown
                            label="Work progress on this page"
                            selectedKey={attentionFilterKey}
                            options={attentionFilterOptions}
                            onChange={(_, option) => updateSearchParams(
                                searchParams,
                                setSearchParams,
                                {
                                    attention: option?.key
                                        ? String(option.key)
                                        : null,
                                },
                            )}
                            styles={{ root: { minWidth: 220 } }}
                        />
                )}
                <TextField
                    label="Search PR or scope"
                    value={searchInput}
                    onChange={(_, value) => setSearchInput(value ?? '')}
                    onKeyDown={event => {
                        if (event.key === 'Enter') {
                            updateSearchParams(searchParams, setSearchParams, {
                                search: searchInput.trim() || null,
                                page: null,
                                prId: null,
                            });
                        }
                    }}
                    styles={{ root: { minWidth: 320 } }}
                />
                <DefaultButton
                    text="Search"
                    onClick={() => updateSearchParams(searchParams, setSearchParams, {
                        search: searchInput.trim() || null,
                        page: null,
                        prId: null,
                    })}
                />
                {hasActiveFilters && (
                    <DefaultButton text="Clear filters" onClick={clearFilters} />
                )}
                {canSelect && stage.key === 'signoff' && (
                    <>
                        <DefaultButton
                            text={bulkEmailLoading
                                ? 'Creating drafts...'
                                : `Email selected PRs (${selectedBulkPrIds.length})`}
                            disabled={
                                bulkInteractionsDisabled
                                || bulkEmailLoading
                                || selectedBulkPrIds.length === 0
                            }
                            onClick={() => void draftBulkReminder()}
                        />
                    </>
                )}
                {canPublish && (
                    <>
                        {reviewerSignalKey.length > 0
                            && selectedDraftItems.length > 0
                            && !publicationSelectionConfirmed
                            && (
                            <DefaultButton
                                text="Use selected drafts for publishing"
                                disabled={bulkInteractionsDisabled || publishing}
                                onClick={() => setPublicationSelectionConfirmed(true)}
                            />
                        )}
                        <DefaultButton
                            text={publishing
                                ? 'Publishing...'
                                : `Publish selected drafts (${selectedDraftItems.length})`}
                            disabled={
                                bulkInteractionsDisabled
                                || publishing
                                || !hasPublicationIntent
                                || selectedDraftItems.length === 0
                                || selectedDraftItems.length > MAX_PUBLICATION_COUNT
                            }
                            onClick={() => setPublishDialogItems(selectedDraftItems)}
                        />
                    </>
                )}
                {!legacyAllActive && stage.key === 'ready' && (
                    <DefaultButton
                        text={enablingAutoComplete ? 'Enabling auto-complete...'
                            : `Enable auto-complete (${selectedBulkItems.length})`}
                        disabled={bulkInteractionsDisabled || enablingAutoComplete
                            || selectedBulkItems.length === 0 || selectedBulkItems.length > MAX_PUBLICATION_COUNT}
                        onClick={() => setAutoCompleteDialogItems(selectedBulkItems)}
                    />
                )}
                {canRepair && (
                    <DefaultButton
                        text={repairing ? `Repairing PRs (${repairProgress}/${repairDialogItems.length})...`
                            : `Repair selected PRs (${selectedBulkItems.length})`}
                        title="Request Guardian rechecks and repairs for the selected PRs."
                        disabled={bulkInteractionsDisabled || repairing || resolveLoading || publishing || enablingAutoComplete
                            || selectedBulkItems.length === 0 || selectedBulkItems.length > MAX_REPAIR_COUNT}
                        onClick={() => setRepairDialogItems(selectedBulkItems)}
                    />
                )}
            </div>

            <div role="status" aria-live="polite" style={{ position: loading && result ? 'static' : 'absolute' }}>
                {loading && result && <Text>Updating results...</Text>}
                {!loading && result && <span className="sr-only">PR dashboard results updated.</span>}
            </div>
            {error && (
                <MessageBar
                    messageBarType={result ? MessageBarType.warning : MessageBarType.error}
                    actions={result
                        ? (
                            <DefaultButton
                                text="Retry"
                                onClick={() => setRefreshToken(value => value + 1)}
                            />
                        )
                        : undefined}
                >
                    {result && lastRefreshedLabel
                        ? `Couldn't refresh. Showing results from ${lastRefreshedLabel}. ${error}`
                        : error}
                </MessageBar>
            )}
            {bulkEmailMessage && (
                <MessageBar onDismiss={() => setBulkEmailMessage(null)}>
                    {bulkEmailMessage}
                </MessageBar>
            )}
            {repairOutcomes.length > 0 && (
                <MessageBar
                    messageBarType={repairOutcomes.some(outcome => !outcome.accepted)
                        ? MessageBarType.warning : MessageBarType.success}
                    onDismiss={() => setRepairOutcomes([])}>
                    <Text block>Repair requests accepted: {repairOutcomes.filter(outcome => outcome.accepted).length} of {repairOutcomes.length}.</Text>
                    {repairOutcomes.map(outcome => <Text block key={outcome.prId}>{outcome.message}</Text>)}
                </MessageBar>
            )}
            {canRepair && selectedBulkItems.length > MAX_REPAIR_COUNT && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Select at most {MAX_REPAIR_COUNT} PRs to repair at a time.
                </MessageBar>
            )}
            {autoCompleteResult && (
                <MessageBar messageBarType={autoCompleteResult.notEnabledCount > 0
                    ? MessageBarType.warning : MessageBarType.success}
                    onDismiss={() => setAutoCompleteResult(null)}>
                    <Text block>{autoCompleteResult.enabledCount} auto-complete enabled
                        {autoCompleteResult.notEnabledCount > 0 ? ` · ${autoCompleteResult.notEnabledCount} not enabled` : ''}
                    </Text>
                    {autoCompleteResult.outcomes.filter(outcome => !outcome.enabled).map(outcome => (
                        <Text block key={`${outcome.taskListId}-${outcome.taskId}-${outcome.prId}`}>
                            PR #{outcome.prId}: {outcome.message}
                        </Text>
                    ))}
                </MessageBar>
            )}
            {!legacyAllActive && stage.key === 'ready' && selectedBulkItems.length > MAX_PUBLICATION_COUNT && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Select at most {MAX_PUBLICATION_COUNT} PRs to enable auto-complete at a time.
                </MessageBar>
            )}
            {publishResult && (
                <MessageBar
                    messageBarType={publishResult.notPublishedCount > 0
                        ? MessageBarType.warning
                        : MessageBarType.success}
                    onDismiss={() => setPublishResult(null)}
                >
                    {(publishResult.requestedCount > 1 || publishResult.publishedCount > 0) && <Text block>
                        {publishResult.publishedCount} published
                        {publishResult.notPublishedCount > 0
                            ? ` · ${publishResult.notPublishedCount} not published`
                            : ''}
                    </Text>}
                    {publishResult.outcomes
                        .filter(outcome => !outcome.published)
                        .map(outcome => (
                            <Text block key={`${outcome.taskListId}-${outcome.taskId}-${outcome.prId}`}>
                                {publishResult.requestedCount === 1 ? 'Not published: ' : ''}
                                {outcome.message.includes(`PR ${outcome.prId}`)
                                    || outcome.message.includes(`PR #${outcome.prId}`)
                                    ? outcome.message
                                    : `PR #${outcome.prId}: ${outcome.message}`}
                            </Text>
                        ))}
                </MessageBar>
            )}
            {canPublish && selectedDraftItems.length > MAX_PUBLICATION_COUNT && (
                <MessageBar messageBarType={MessageBarType.warning}>
                    Select at most {MAX_PUBLICATION_COUNT} draft PRs to publish at a time.
                    {' '}{selectedDraftItems.length} drafts are currently selected.
                </MessageBar>
            )}
            {canPublish && selectedNonDraftCount > 0 && hasPublicationIntent && (
                <MessageBar messageBarType={MessageBarType.info}>
                    {selectedNonDraftCount} selected PR
                    {selectedNonDraftCount === 1 ? ' is' : 's are'} already published
                    and will not be submitted.
                </MessageBar>
            )}
            {loading && !result ? (
                <Spinner size={SpinnerSize.large} label="Loading PR dashboard..." />
            ) : result ? (
                <>
                    {visibleItems.length === 0 ? (
                        <Stack
                            horizontal
                            wrap
                            verticalAlign="center"
                            tokens={{ childrenGap: 12 }}
                            styles={{
                                root: {
                                    border: '1px solid #d1d1d1',
                                    padding: 16,
                                },
                            }}
                        >
                            <Text>
                                {selectedAttentionLevels !== undefined && result.items.length > 0
                                    ? `No PRs with "${attentionFilterLabel}" are loaded on this page. Other pages may contain matches.`
                                    : taskListId !== null
                                    ? `No ${stageLabel.toLowerCase()} PRs for this task list.`
                                    : 'No PRs match the current filters.'}
                            </Text>
                            {taskListId !== null && (
                                <DefaultButton
                                    text={view === 0 ? 'View completed PRs' : 'View draft PRs'}
                                    onClick={() => updateSearchParams(
                                        searchParams,
                                        setSearchParams,
                                        {
                                            stage: view === 0 ? 'completed' : 'draft',
                                            view: null,
                                            page: null,
                                            prId: null,
                                        },
                                    )}
                                />
                            )}
                            {hasActiveFilters && (
                                <DefaultButton text="Clear filters" onClick={clearFilters} />
                            )}
                        </Stack>
                    ) : (
                        <div
                            data-testid="autopr-scroll-container"
                            data-is-scrollable={false}
                            aria-busy={loading}
                            style={{
                                minWidth: 0,
                                maxWidth: '100%',
                                opacity: loading ? 0.72 : 1,
                                overflowX: 'auto',
                            }}
                        >
                            <DetailsList
                                setKey="autopr-dashboard"
                                items={sortedVisibleItems}
                                columns={view === 0 ? activeColumns.filter(column =>
                                    column.key !== 'prState'
                                    && (column.key !== 'age' || !showPublishDate)
                                    && (column.key !== 'published' || showPublishDate)
                                    && (column.key !== 'blocked' || legacyAllActive || stage.key !== 'ready-to-publish')
                                    && (column.key !== 'status' || showProgress)
                                    && (column.key !== 'signedOff' || showReviewers)) : completedColumns}
                                selection={bulkSelection}
                                selectionMode={showSelection
                                    ? SelectionMode.multiple
                                    : SelectionMode.none}
                                selectionZoneProps={{ selectionClearedOnSurfaceClick: false }}
                                layoutMode={DetailsListLayoutMode.fixedColumns}
                                onColumnResize={handleColumnResize}
                                listProps={listProps}
                            />
                        </div>
                    )}
                    <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 12 }}>
                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <DefaultButton
                                text="Previous"
                                disabled={page <= 1}
                                onClick={() => updateSearchParams(searchParams, setSearchParams, {
                                    page: String(page - 1),
                                    prId: null,
                                })}
                            />
                            <DefaultButton
                                text="Next"
                                disabled={page * pageSize >= result.totalCount}
                                onClick={() => updateSearchParams(searchParams, setSearchParams, {
                                    page: String(page + 1),
                                    prId: null,
                                })}
                            />
                        </Stack>
                        <Text>
                            Showing {visibleItems.length} of {result.items.length} PRs on this page
                            {' · '}{result.totalCount} match server filters
                        </Text>
                    </Stack>
                </>
            ) : null}

            <Panel
                isOpen={selectedPrId !== null}
                onDismiss={closeDetails}
                isLightDismiss
                closeButtonAriaLabel="Close"
                type={PanelType.medium}
                headerText={selectedPrId ? `PR ${selectedPrId}` : 'PR details'}
                isFooterAtBottom
                onRenderFooterContent={() => selected ? (
                    <Stack tokens={{ childrenGap: 12 }}>
                        <Stack horizontal wrap tokens={{ childrenGap: 8 }}>
                            {DRAFT_STATUSES.has(selected.status) && (
                                <PrimaryButton
                                    text={publishing ? 'Publishing...' : 'Publish'}
                                    disabled={publishing || panelInteractionsDisabled}
                                    onClick={() => setPublishDialogItems([selected])}
                                />
                            )}
                            {selected.canResolve && (
                                <DefaultButton
                                    text={resolveLoading ? 'Resolving...' : 'Resolve'}
                                    title="Request a Guardian recheck and repair; this does not directly resolve ADO threads."
                                    disabled={resolveLoading || repairing || panelInteractionsDisabled}
                                    onClick={() => void resolvePullRequest()}
                                />
                            )}
                        </Stack>
                        {resolveFeedback && (
                            <MessageBar
                                messageBarType={resolveFeedback.type}
                                role={resolveFeedback.type === MessageBarType.error
                                    ? 'alert'
                                    : 'status'}
                            >
                                {resolveFeedback.message}
                            </MessageBar>
                        )}
                        {selected.status !== 7 && selected.status !== 8 && (
                            <Text variant="small">
                                {panelInteractionsDisabled
                                    ? 'Refresh the dashboard before taking action.'
                                    : 'Publish checks live ADO state. Resolve requests a Guardian recheck and repair.'}
                            </Text>
                        )}
                        <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                            {buildAdoPullRequestUrl(service, selected.prId) && (
                                <a
                                    href={buildAdoPullRequestUrl(service, selected.prId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Open in Azure DevOps
                                </a>
                            )}
                            <Link to={`/service/${serviceName}/agenttasks/task/${selected.taskId}?taskListId=${selected.taskListId}`}>
                                Open task view
                            </Link>
                        </Stack>
                    </Stack>
                ) : null}
            >
                {detailsLoading && (
                    <Spinner size={SpinnerSize.medium} label={`Loading PR ${selectedPrId}...`} />
                )}
                {detailsError && (
                    <MessageBar messageBarType={MessageBarType.error}>
                        {detailsError} Refresh to retry.
                    </MessageBar>
                )}
                {selected && (
                    <AutoPrDetails item={selected} serviceName={serviceName!} refreshToken={refreshToken}>
                    <Stack tokens={{ childrenGap: 20 }} styles={{ root: { paddingTop: 12 } }}>
                        <Stack tokens={{ childrenGap: 10 }}>
                            <Text styles={{ root: { fontWeight: 600, color: '#605e5c' } }}>
                                {getPrStateLabel(selected.status)}
                            </Text>
                            <Text block styles={{ root: { overflowWrap: 'anywhere', lineHeight: '20px' } }}>
                                {selected.scope}
                            </Text>
                        </Stack>
                        <Stack
                            role="group"
                            aria-label="Work progress"
                            tokens={{ childrenGap: 8 }}
                            styles={{ root: {
                                background: selectedProgress?.background,
                                borderRadius: 8,
                                padding: 16,
                            } }}
                        >
                            <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
                                Work progress
                            </Text>
                            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>
                                {selectedProgress?.label}
                            </Text>
                            <Text>{selectedProgress?.description}</Text>
                        </Stack>
                        {selected.status !== 7
                            && selected.status !== 8
                            && selectedBlockers.length > 0 && (
                            <Stack
                                data-testid={`autopr-panel-blockers-${selected.prId}`}
                                tokens={{ childrenGap: 8 }}
                            >
                                <Text><b>Blocked on</b></Text>
                                <BlockerCards item={selected} />
                                {getReminderReviewers(selected).length > 0 && (
                                    <PullRequestReviewerEmailButton
                                        service={service}
                                        taskListId={selected.taskListId}
                                        taskId={selected.taskId}
                                        prId={selected.prId}
                                        scope={selected.scope}
                                        attentionSummary={selected.attentionSummary}
                                        dateCreated={selected.dateCreated}
                                        signals={selected.signals}
                                        reviewInspectionComplete={selected.reviewInspectionComplete}
                                        disabled={panelInteractionsDisabled}
                                    />
                                )}
                            </Stack>
                        )}
                        <Stack role="group" aria-label="Reviewers" tokens={{ childrenGap: 8 }}>
                            <Text><b>Reviewers</b></Text>
                            <ReviewerList signals={selected.signals} />
                        </Stack>
                        {selected.repairWorkItemId && (
                            <Stack tokens={{ childrenGap: 6 }} styles={{ root: { borderTop: '1px solid #edebe9', paddingTop: 12 } }}>
                                <PullRequestRepairProgress progress={selected.repairProgress} />
                            </Stack>
                        )}
                        {selected.status === 7 && (
                            <Text><b>Completed:</b> {formatPrLocalDate(selected.completedDate)}</Text>
                        )}
                        <Stack tokens={{ childrenGap: 4 }} styles={{ root: { color: '#605e5c', paddingBottom: 16 } }}>
                            <Text variant="small">
                                Last checked: {selected.dateLastChecked ? formatPrLocalDate(selected.dateLastChecked) : 'Not yet checked'}
                            </Text>
                            <Text variant="small">
                                Next eligible check: {getNextEligibleCheck(selected)}
                            </Text>
                        </Stack>
                    </Stack>
                    </AutoPrDetails>
                )}
            </Panel>
            <Dialog
                hidden={repairDialogItems.length === 0}
                onDismiss={() => { if (!repairing) setRepairDialogItems([]); }}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: `Repair ${repairDialogItems.length} selected PR${repairDialogItems.length === 1 ? '' : 's'}?`,
                    subText: 'Guardian will recheck each PR and start the applicable background resolution, Agency repair, or validation refresh. This does not publish or merge the PRs.',
                }}
                modalProps={{ isBlocking: repairing }}
            >
                <ul style={{ maxHeight: 240, overflowY: 'auto' }}>
                    {repairDialogItems.map(item => (
                        <li key={item.prId}>PR #{item.prId} - {item.scope}</li>
                    ))}
                </ul>
                {repairing && <Spinner label={`Requesting repair ${repairProgress} of ${repairDialogItems.length}...`} />}
                <DialogFooter>
                    <PrimaryButton text={repairing ? 'Requesting repairs...' : 'Check and repair selected PRs'}
                        disabled={repairing} onClick={() => void repairSelectedPullRequests()} />
                    <DefaultButton text="Cancel" disabled={repairing} onClick={() => setRepairDialogItems([])} />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={autoCompleteDialogItems.length === 0}
                onDismiss={() => { if (!enablingAutoComplete) setAutoCompleteDialogItems([]); }}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: `Enable auto-complete for ${autoCompleteDialogItems.length} PR${autoCompleteDialogItems.length === 1 ? '' : 's'}?`,
                    subText: 'Guardian will recheck live Azure DevOps state, validation, repairs and required signoffs. Eligible PRs will merge automatically when all final Azure DevOps policies pass; no policies are bypassed.',
                }}
                modalProps={{ isBlocking: enablingAutoComplete }}
            >
                <ul>
                    {autoCompleteDialogItems.map(item => (
                        <li key={item.prId}>PR #{item.prId} - {item.scope}</li>
                    ))}
                </ul>
                <DialogFooter>
                    <PrimaryButton text={enablingAutoComplete ? 'Enabling auto-complete...' : 'Check and enable auto-complete'}
                        disabled={enablingAutoComplete} onClick={() => void enableAutoComplete()} />
                    <DefaultButton text="Cancel" disabled={enablingAutoComplete} onClick={() => setAutoCompleteDialogItems([])} />
                </DialogFooter>
            </Dialog>
            <Dialog
                hidden={publishDialogItems.length === 0}
                onDismiss={() => {
                    if (!publishing) setPublishDialogItems([]);
                }}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: publishDialogItems.length === 1
                        ? `Publish PR ${publishDialogItems[0]?.prId ?? ''}?`
                        : `Publish ${publishDialogItems.length} draft PRs?`,
                    subText:
                        'Guardian will check current Azure DevOps state and publish only eligible drafts. Running or pending required validations block publication. Expired-only or confirmed capacity-throttled validations and missing reviewer signoffs may remain. Publishing opens a PR for review; it does not complete or merge it.',
                }}
                modalProps={{ isBlocking: publishing }}
                minWidth={620}
            >
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text block>PRs to check and publish:</Text>
                    <ul style={{ marginBottom: 0, marginTop: 0, maxHeight: 240, overflowY: 'auto' }}>
                        {publishDialogItems.map(item => (
                            <li key={`${item.taskListId}-${item.taskId}-${item.prId}`}>
                                PR #{item.prId} - {item.scope}
                            </li>
                        ))}
                    </ul>
                    {selectedNonDraftCount > 0 && publishDialogItems.length > 1 && (
                        <Text variant="small">
                            {selectedNonDraftCount} selected non-draft PR
                            {selectedNonDraftCount === 1 ? ' is' : 's are'} excluded.
                        </Text>
                    )}
                </Stack>
                <DialogFooter>
                    <PrimaryButton
                        text={publishing
                            ? 'Publishing...'
                            : `Check and publish ${publishDialogItems.length} PR${
                                publishDialogItems.length === 1 ? '' : 's'
                            }`}
                        disabled={publishing}
                        onClick={() => void publishPullRequests()}
                    />
                    <DefaultButton
                        text="Cancel"
                        disabled={publishing}
                        onClick={() => setPublishDialogItems([])}
                    />
                </DialogFooter>
            </Dialog>
        </PageShell>
    );
}
